<template>
  <Teleport to="body">
    <div
      class="modal is-active recipe-import-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-recipe-modal-title"
      @keydown.esc="handleClose"
      @keydown.enter="handleEnterKey"
    >
      <div class="modal-background" @click="handleClose"></div>
      <div ref="modalRef" class="modal-card">
        <header class="modal-card-head">
          <p id="import-recipe-modal-title" class="modal-card-title">
            {{ previewData ? `Import from "${previewData.recipe.name}"` : "Import from Recipe" }}
          </p>
          <button class="delete" aria-label="close" @click="handleClose"></button>
        </header>

        <section class="modal-card-body">
          <template v-if="!previewData">
            <div class="field">
              <label for="recipe-search-input" class="label">Search Recipes</label>
              <div class="control has-icons-left">
                <input
                  id="recipe-search-input"
                  v-model="query"
                  class="input"
                  type="text"
                  placeholder="Search by name..."
                  @input="debouncedSearch"
                />
                <span class="icon is-small is-left">
                  <i class="fas fa-search"></i>
                </span>
              </div>
            </div>

            <div v-if="loading" class="has-text-centered py-4">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
            </div>

            <div
              v-else-if="listedRecipes.length === 0"
              class="has-text-centered py-4 has-text-grey"
            >
              No recipes found.
            </div>

            <div v-else class="list is-hoverable">
              <a
                v-for="recipe in listedRecipes"
                :key="recipe.id"
                class="list-item"
                :class="{ 'is-last-imported': isLastImported(recipe) }"
                @click="selectRecipe(recipe)"
              >
                <div class="is-flex is-justify-content-space-between is-align-items-center">
                  <div>
                    <strong>{{ recipe.name }}</strong>
                    <span
                      v-if="isLastImported(recipe)"
                      class="tag mx-2 last-imported-tag"
                      title="The last import into this label used this recipe"
                    >
                      Last imported
                    </span>
                    <br />
                    <small class="has-text-grey">Updated {{ formatDate(recipe.updated_at) }}</small>
                  </div>
                  <span class="icon is-small">
                    <i class="fas fa-chevron-right"></i>
                  </span>
                </div>
              </a>
            </div>
          </template>

          <template v-else>
            <div class="notification is-info is-light mb-4">
              Select which data to import. Click a card to expand and see details.
            </div>

            <div
              v-if="anyWillOverwrite"
              class="notification is-warning is-light bordered-left mb-4"
            >
              <strong>Some information will be overwritten.</strong>
              If you want to avoid that, uncheck items below that say
              <span class="overwrite-badge">Overwrites current</span>
            </div>

            <div v-if="loadingPreview" class="has-text-centered py-4">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
              <p class="mt-2">Loading preview...</p>
            </div>

            <div v-else>
              <button
                class="button is-info is-outlined is-fullwidth is-mini mb-3"
                @click="toggleAllCategories"
              >
                {{ allDataCategoriesSelected ? "Deselect All" : "Select All" }}
              </button>

              <ImportCategoryCard
                v-for="key in orderedCategoryKeys"
                :key="key"
                v-model="selectedCategories[key]"
                :category="previewData.categories[key]"
                :category-key="key"
                :disabled="!hasData(previewData.categories[key])"
                :will-overwrite="currentLabelHasData[key] && hasData(previewData.categories[key])"
              />
            </div>
          </template>
        </section>

        <footer class="modal-card-foot is-justify-content-space-between">
          <div>
            <button v-if="previewData" class="button is-outlined" @click="previewData = null">
              <span class="icon"><i class="fas fa-arrow-left"></i></span>
              <span>Back</span>
            </button>
          </div>
          <div>
            <button class="button is-outlined" @click="handleClose">Cancel</button>
            <button
              v-if="previewData"
              class="button is-info ml-2"
              :disabled="!anySelected || importing"
              @click="doImport"
            >
              <span v-if="importing" class="icon"><i class="fas fa-spinner fa-spin"></i></span>
              <span>{{ importing ? "Importing..." : "Import Selected" }}</span>
              <span class="icon"><i class="fas fa-arrow-right"></i></span>
            </button>
          </div>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
  import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
  import { debounce } from "lodash-es";
  import ImportCategoryCard from "./import-category-card.vue";
  import { hasContentBeyondPrefix } from "./utils/formatting";
  import { useFocusTrap } from "../lib/use-focus-trap";
  import { useSupplementLabelStore } from "../stores/supplement-label-store";
  import { SupplementApi } from "../stores/supplement-api";
  import { Flash } from "~/jose_modern/flash";

  const props = defineProps({
    userId: { type: [String, Number], required: true },
    labelId: { type: [String, Number], required: true },
    initialRecipeId: { type: [String, Number], default: null },
  });

  const store = useSupplementLabelStore();

  const emit = defineEmits(["close", "imported"]);

  let searchController = null;
  let previewController = null;
  let importController = null;

  const modalRef = ref(null);
  const previousActiveElement = ref(null);
  const query = ref("");
  const recipes = ref([]);
  const loading = ref(false);
  const loadingPreview = ref(false);
  const importing = ref(false);
  const previewData = ref(null);

  const orderedCategoryKeys = [
    "serving_info",
    "macronutrients",
    "micronutrients",
    "non_dv",

    "other_ingredients",
    "allergens",
    "facility_allergens",
    "business_info",
  ];

  useFocusTrap(
    () => modalRef.value,
    () => true
  );

  const selectedCategories = reactive({
    macronutrients: true,
    micronutrients: true,
    non_dv: true,
    serving_info: true,
    other_ingredients: true,
    allergens: false,
    facility_allergens: false,
    business_info: false,
  });

  const isLastImported = (recipe) => {
    return !!store.lastImportedRecipe && Number(recipe.id) === Number(store.lastImportedRecipe.id);
  };

  const listedRecipes = computed(() => {
    const pinned = store.lastImportedRecipe;
    if (!pinned || query.value.trim()) return recipes.value;

    const found = recipes.value.find((recipe) => isLastImported(recipe));
    return [found || pinned, ...recipes.value.filter((recipe) => !isLastImported(recipe))];
  });

  const anySelected = computed(() => {
    if (!previewData.value) return false;
    return Object.entries(selectedCategories).some(([key, selected]) => {
      return selected && hasData(previewData.value.categories[key]);
    });
  });

  const hasData = (category) => {
    if (!category) return false;
    if (category.lines && category.lines.length > 0) return true;
    if (category.data) return true;
    return false;
  };

  const currentLabelHasData = computed(() => ({
    serving_info: !!(store.servingSizeText || store.servingsPerContainer),
    macronutrients: store.dietaryLines?.some((l) => !!l.amount),
    micronutrients: store.dvLines?.length > 0,
    non_dv: store.linesWithoutDv?.length > 0,
    other_ingredients: hasContentBeyondPrefix(store.otherIngredients),
    allergens: hasContentBeyondPrefix(store.allergens),
    facility_allergens: hasContentBeyondPrefix(store.facilityAllergens),
    business_info: hasContentBeyondPrefix(store.businessInfo),
  }));

  const anyWillOverwrite = computed(() => {
    if (!previewData.value) return false;
    return Object.entries(selectedCategories).some(([key, selected]) => {
      return (
        selected && currentLabelHasData.value[key] && hasData(previewData.value.categories[key])
      );
    });
  });

  const categoriesWithData = computed(() => {
    if (!previewData.value) return [];
    return orderedCategoryKeys.filter((key) => hasData(previewData.value.categories[key]));
  });

  const allDataCategoriesSelected = computed(() => {
    return (
      categoriesWithData.value.length > 0 &&
      categoriesWithData.value.every((key) => selectedCategories[key])
    );
  });

  const toggleAllCategories = () => {
    const newValue = !allDataCategoriesSelected.value;
    categoriesWithData.value.forEach((key) => {
      selectedCategories[key] = newValue;
    });
  };

  const searchRecipes = async () => {
    if (searchController) searchController.abort();
    searchController = new AbortController();

    loading.value = true;
    try {
      recipes.value = await SupplementApi.searchRecipes(props.userId, query.value, {
        signal: searchController.signal,
      });
    } catch (error) {
      if (error.name !== "AbortError") {
        Flash.error(error.message || "Failed to search recipes");
        recipes.value = [];
      }
    } finally {
      loading.value = false;
    }
  };

  const debouncedSearch = debounce(searchRecipes, 300);

  const selectRecipe = async (recipe) => {
    if (previewController) previewController.abort();
    previewController = new AbortController();

    loadingPreview.value = true;
    previewData.value = { recipe: { id: recipe.id, name: recipe.name }, categories: {} };

    try {
      previewData.value = await SupplementApi.previewImportFromRecipe(props.labelId, recipe.id, {
        signal: previewController.signal,
      });
      Object.keys(selectedCategories).forEach((key) => {
        selectedCategories[key] = hasData(previewData.value.categories[key]);
      });
    } catch (error) {
      if (error.name !== "AbortError") {
        Flash.error(error.message || "Failed to load preview");
        previewData.value = null;
      }
    } finally {
      loadingPreview.value = false;
    }
  };

  const doImport = async () => {
    if (!previewData.value) return;

    if (importController) importController.abort();
    importController = new AbortController();

    importing.value = true;
    try {
      const data = await SupplementApi.importFromRecipe(
        props.labelId,
        previewData.value.recipe.id,
        { ...selectedCategories },
        {
          signal: importController.signal,
        }
      );

      emit("imported", data);
      Flash.success(`Imported data from "${previewData.value.recipe.name}"`);
    } catch (error) {
      if (error.name !== "AbortError") {
        Flash.error(error.message || "Import failed");
      }
    } finally {
      importing.value = false;
    }
  };

  const handleClose = () => {
    if (!importing.value) {
      emit("close");
      nextTick(() => {
        previousActiveElement.value?.focus?.();
      });
    }
  };

  const handleEnterKey = (event) => {
    const hasOpenDropdown = modalRef.value?.querySelector(".dropdown-content");
    if (!hasOpenDropdown && previewData.value && anySelected.value && !importing.value) {
      event.preventDefault();
      doImport();
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  onMounted(() => {
    previousActiveElement.value = document.activeElement;
    document.body.classList.add("side-panel-scroll-lock");

    if (props.initialRecipeId) {
      // Skip search and go straight to preview for the specified recipe
      selectRecipe({ id: props.initialRecipeId, name: "" });
    } else {
      searchRecipes();
    }
  });

  onBeforeUnmount(() => {
    document.body.classList.remove("side-panel-scroll-lock");
    searchController?.abort();
    previewController?.abort();
    importController?.abort();
  });
</script>

<style lang="scss" scoped>
  @import "~jose_modern/css";
  @import "~/label/css/labels/supplements/supplement_mixins";

  .modal-card {
    max-width: 600px;
    width: 100%;
  }
  // A recipe name long enough to fill the head otherwise pushes the close button out of it.
  .modal-card-head {
    align-items: flex-start;
    gap: 0.75rem;
  }
  .modal-card-title {
    flex-shrink: 1;
    line-height: 1.25;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .modal-card-head .delete {
    flex-shrink: 0;
  }
  // `list` and `list-item` are styled nowhere else: Bulma dropped its list component after 0.7.
  .list {
    margin: 0 -0.5em;
  }
  .list-item {
    display: block;
    cursor: pointer;
    padding: 0.4em 0.5em;
    border-top: 1px solid #e5e5e5;

    &:first-child {
      border-top: none;
    }

    &:hover {
      background: $white-bis;
    }

    &.is-last-imported {
      background: $warning-bg-light;

      &:hover {
        background: darken($warning-bg-light, 4%);
      }
    }
  }
  .last-imported-tag {
    background-color: $warning-bg;
    color: #947400;
  }
  .recipe-import-modal {
    z-index: 10000 !important;
  }
  .overwrite-badge {
    font-weight: 600;
    font-style: italic;
    color: #d97706;
    background-color: #fff7ed;
    padding: 0.1em 0.25em;
    border-radius: 3px;
  }
</style>
